import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Newspaper } from 'lucide-react';
import newsService, { NewsArticle } from '../../services/newsService';
import ThumbnailImage from '../ThumbnailImage';
import DynamicSlider from '../slider/DynamicSlider';
import { t } from '../../i18n';

/** Slider-Location (Web + App an derselben Stelle, Marcel 2026-09-15): unter dem Hauptartikel */
export const NEWS_LEAD_SLIDER_LOCATION = 'home_news_lead';

const ACCENT = '#3fced1';

const articleHref = (item?: NewsArticle) => (item?.slug ? `/news/${item.slug}` : '#');

/**
 * Aufmacher-Bereich im Zeitungslayout (statt Hero): eine große Lead-News-Karte
 * links (Bild + Gradient-Overlay + Kategorie-Badge) und rechts 4 kompakte
 * Sekundär-News mit Thumbnail. Blendet sich aus, wenn keine News da sind.
 *
 * CLS-Fix (2026-09-01): solange die News LADEN, reserviert ein Skeleton im
 * identischen Grid-Layout (Lead-Karte in Endhöhe + 4 Sekundär-Zeilen) den
 * Platz — News sind der Normalfall auf der Startseite.
 */
const LeadStoryGrid: React.FC<{ articles: NewsArticle[]; loading?: boolean }> = ({
  articles,
  loading = false,
}) => {
  // Marcel 2026-09-29: mit gesetztem Slider rechts nur 3 Nebenartikel, sonst wird der Hauptartikel links zu hoch
  const [sliderCount, setSliderCount] = useState(0);
  if (articles.length === 0) {
    if (!loading) return null;
    return (
      <section aria-label={t('web.component.news-section.top-news')} aria-hidden="true">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.news-section.news')}</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.55fr_1fr]">
          {/* Lead-Karte: fest 16:9 wie die echte Karte (Marcel 2026-09-22) */}
          <div className="aspect-video w-full animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900" />
          {/* 4 Sekundär-Zeilen mit identischer Struktur (Bild 16:9, w-28 + p-3) */}
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-1 items-center gap-3 rounded-2xl border border-zinc-800 bg-zinc-900 p-3"
              >
                <div className="aspect-video w-28 shrink-0 animate-pulse rounded-lg bg-zinc-800" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-3.5 w-4/5 animate-pulse rounded bg-zinc-800" />
                  <div className="h-3 w-2/5 animate-pulse rounded bg-zinc-800" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const lead = articles[0];
  const secondary = articles.slice(1, sliderCount > 0 ? 4 : 5);
  const leadImage = newsService.getImageUrl(lead);
  const leadSource = newsService.getDisplaySource(lead);

  return (
    <section aria-label={t('web.component.news-section.top-news')}>
      {/* Section-Header wie in der App: links Titel, rechts "Alle News" */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">{t('web.component.news-section.news')}</h2>
        <Link
          to="/news"
          className="flex items-center gap-1 text-[13px] font-semibold transition-opacity hover:opacity-80"
          style={{ color: ACCENT }}
        >
          {t('web.component.lead-story-grid.alle-news')}
          <ArrowRight size={14} />
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.55fr_1fr]">
        {/* Lead-Story */}
        <Link to={articleHref(lead)} className="group block lg:h-full">
          {/* Fix 16:9 (Marcel 2026-09-22, Web und App identisch): keine freien Mindesthöhen mehr.
              Desktop (Marcel 2026-09-29): der Hauptartikel füllt die Höhe der rechten Spalte (Slider + Nebenartikel),
              sonst bleibt unter ihm Leerraum, sobald ein Slider gesetzt ist. */}
          <article className="flex w-full flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 lg:h-full lg:min-h-[360px]" data-testid="lead-story">
            {/* Bild oben, fest 16:9 (Desktop füllt die Resthöhe), KEIN Text im Bild (Marcel 2026-10-05: Inhalt getrennt) */}
            <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-zinc-900 lg:aspect-auto lg:min-h-0 lg:flex-1" data-testid="lead-story-image">
              {leadImage ? (
                <img
                  src={leadImage}
                  alt={newsService.formatTitle(lead)}
                  // LCP-Element der Startseite: sofort + mit hoher Priorität laden
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
                  <Newspaper size={48} className="text-zinc-700" />
                </div>
              )}
            </div>
            {/* Inhalt unter dem Bild: Chip, Titel, Zeit · Quelle */}
            <div className="p-5 sm:p-6" data-testid="lead-story-content">
              {lead.category && (
                <span
                  className="mb-2 inline-flex rounded px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-black"
                  style={{ backgroundColor: ACCENT }}
                >
                  {lead.category}
                </span>
              )}
              <h2 className="line-clamp-3 text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-3xl">
                {newsService.formatTitle(lead)}
              </h2>
              <div className="mt-3 flex items-center gap-3 text-xs text-gray-400 sm:text-sm">
                <span className="flex items-center gap-1.5">
                  <Clock size={13} />
                  {newsService.formatArticleTime(lead)}
                </span>
                {leadSource && <span className="truncate">{leadSource}</span>}
              </div>
            </div>
          </article>
        </Link>

        {/* Rechte Spalte: Slider (Mobile direkt unter dem Hauptartikel, Desktop
            rechts neben ihm über den Nebenartikeln – Marcel 2026-09-15), dann Sekundär-News */}
        <div className="flex flex-col gap-3">
          <DynamicSlider location={NEWS_LEAD_SLIDER_LOCATION} className="w-full" onSlidesChange={setSliderCount} />
          {secondary.map((item) => (
            <SecondaryStory key={item._id || item.id || item.slug} article={item} />
          ))}
        </div>
      </div>
    </section>
  );
};

const SecondaryStory: React.FC<{ article: NewsArticle }> = ({ article }) => {
  const image = newsService.getImageUrl(article);
  return (
    <Link
      to={articleHref(article)}
      className="group flex flex-1 items-stretch gap-3 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-colors hover:border-[#3fced1]/50"
      data-testid="secondary-story"
    >
      {image ? (
        // Marcel 2026-09-29: das Bild IST die linke Kante der Karte (kein Innenabstand, volle Kartenhöhe); 16:9-Thumbnail
        <ThumbnailImage
          src={image}
          thumbSize="wide"
          alt=""
          loading="lazy"
          className="w-32 shrink-0 self-stretch object-cover"
        />
      ) : (
        <div className="flex w-32 shrink-0 items-center justify-center self-stretch bg-zinc-800">
          <Newspaper size={18} className="text-zinc-600" />
        </div>
      )}
      <div className="min-w-0 py-3 pr-3">
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {newsService.formatTitle(article)}
        </h3>
        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
          <span>{newsService.formatArticleTime(article)}</span>
          {article.category && <span className="truncate">{article.category}</span>}
        </div>
      </div>
    </Link>
  );
};

export default LeadStoryGrid;
